<template lang="pug">
.guest-market-rules
    .gmr-header
        .gmr-header__left
            h4.gmr-header__title {{ $t('Marktregeln für Gäste') }}
            p.gmr-header__hint {{ $t('gmr-hint') }}
        el-button(
            size="mini"
            type="primary"
            icon="el-icon-fa-plus fas"
            @click="openCreate"
        ) {{ $t('Regel hinzufügen') }}

    el-table.gmr-table(
        v-if="rules.length || loading"
        :data="rules"
        size="small"
        stripe
        v-loading="loading"
        :row-style="{ cursor: 'pointer' }"
        @row-click="handleRowClick"
    )
        el-table-column(prop="priority" :label="$t('Priorität')" width="100" align="center")
            template(slot-scope="scope")
                span.gmr-priority {{ scope.row.priority }}
        el-table-column(prop="condition_type" :label="$t('Bedingung')" width="130")
            template(slot-scope="scope")
                el-tag(
                    size="mini"
                    :type="scope.row.condition_type === 'country' ? 'success' : 'warning'"
                    effect="light"
                ) {{ $t(scope.row.condition_type) }}
                span.gmr-condition-value  {{ scope.row.condition_value }}
        el-table-column(:label="$t('Zielgruppe')")
            template(slot-scope="scope")
                span.gmr-group-name {{ scope.row.targetCustomerGroup.name }}
                span.gmr-currency  · {{ scope.row.targetCustomerGroup.currency.iso4217 }}
        el-table-column(:label="$t('Geltungsbereich')" min-width="140")
            template(slot-scope="scope")
                span(v-if="scope.row.domain") {{ scope.row.domain.base_url }}
                span.gmr-all-domains(v-else) {{ $t('Alle Domains') }}
        el-table-column(width="50" align="right")
            template(slot-scope="scope")
                el-button(circle type="danger" size="mini" icon="el-icon-fa-trash-alt far" @click.stop="askDelete(scope.row)")

    .gmr-empty(v-else)
        i.el-icon-fa-map-signs.fas.gmr-empty__icon
        p.gmr-empty__text {{ $t('Noch keine Regeln.') }}
        p.gmr-empty__sub {{ $t('Besucher fallen auf die domain-spezifische Gast-Kundengruppe oder den Shop-Standard zurück.') }}

    el-dialog(
        :title="editing.id ? $t('Regel bearbeiten') : $t('Neue Regel')"
        :visible.sync="formVisible"
        width="540px"
        :close-on-click-modal="false"
    )
        el-form(
            ref="ruleForm"
            :model="editing"
            :rules="validationRules"
            label-position="top"
        )
            .gmr-form-row
                el-form-item.gmr-form-col(:label="$t('Priorität')" prop="priority")
                    el-input-number(v-model="editing.priority" :min="0" :max="999" controls-position="right")
                el-form-item.gmr-form-col.gmr-form-col--grow(:label="$t('Bedingungstyp')" prop="condition_type")
                    el-select(v-model="editing.condition_type" style="width:100%")
                        el-option(value="country" :label="$t('Land (ISO-Code)')")
                        el-option(value="language" :label="$t('Sprache (Basis-Tag)')")
            el-form-item(:label="$t('Wert')" prop="condition_value")
                el-input(
                    v-model="editing.condition_value"
                    :placeholder="editing.condition_type === 'country' ? 'DE, CH, AT …' : 'de, en, fr …'"
                    :maxlength="32"
                )
                    template(slot="prepend")
                        i(:class="editing.condition_type === 'country' ? 'el-icon-fa-globe fas' : 'el-icon-fa-language fas'")
            el-form-item(:label="$t('Ziel-Kundengruppe')" prop="targetCustomerGroup.id")
                el-select(v-model="editing.targetCustomerGroup.id" filterable style="width:100%")
                    el-option(
                        v-for="group in guestEligibleGroups"
                        :key="group.id"
                        :label="group.currency ? group.name + ' (' + group.currency.iso4217 + ')' : group.name"
                        :value="group.id"
                    )
            el-form-item(:label="$t('Domain')" prop="domain.id")
                el-select(v-model="editing.domain.id" clearable filterable :placeholder="$t('Alle Domains')" style="width:100%")
                    el-option(
                        v-for="domain in domainOptions"
                        :key="domain.id"
                        :label="domain.base_url"
                        :value="domain.id"
                    )
                p.gmr-field-hint {{ $t('domain-hint') }}
        span(slot="footer")
            el-button(size="mini" icon="el-icon-fa-times fas" @click="formVisible = false") {{ $t('Abbrechen') }}
            el-button(size="mini" type="primary" icon="el-icon-fa-save fas" :loading="saving" @click="submit") {{ $t('Speichern') }}
</template>

<i18n>
{
    "de": {
        "gmr-hint": "Werden in Prioritätsreihenfolge geprüft, nachdem die Domain des Besuchers ausgewertet wurde. Die erste passende Regel gewinnt – niedrigere Prioritätszahl bedeutet höhere Priorität. Länder-Regeln verwenden GeoIP, Sprach-Regeln den Accept-Language-Header. Ohne Treffer greift die Standard-Kundengruppe des Shops."
    },
    "en": {
        "Marktregeln für Gäste": "Guest Market Rules",
        "Regel hinzufügen": "Add Rule",
        "gmr-hint": "Evaluated in priority order after the visitor's domain pin is checked. The first matching rule wins — a lower priority number means higher precedence. Country rules use GeoIP, language rules use the Accept-Language header. If no rule matches, the shop's default customer group applies.",
        "Priorität": "Priority",
        "Bedingung": "Condition",
        "Bedingungstyp": "Condition type",
        "Wert": "Value",
        "Zielgruppe": "Target group",
        "Geltungsbereich": "Scope",
        "Alle Domains": "All domains",
        "Noch keine Regeln.": "No rules yet.",
        "Besucher fallen auf die domain-spezifische Gast-Kundengruppe oder den Shop-Standard zurück.": "Visitors fall back to each domain's pinned guest group, or to the shop default.",
        "Regel bearbeiten": "Edit rule",
        "Neue Regel": "New rule",
        "Land (ISO-Code)": "Country (ISO code)",
        "Sprache (Basis-Tag)": "Language (base tag)",
        "Ziel-Kundengruppe": "Target customer group",
        "domain-hint": "Restrict to a specific domain, or leave empty to apply this rule to all domains of the shop.",
        "Speichern": "Save",
        "Abbrechen": "Cancel",
        "country": "Country",
        "language": "Language",
        "Diese Regel löschen?": "Delete this rule?",
        "Löschen": "Delete",
        "Regel gelöscht": "Rule deleted",
        "Regel konnte nicht gespeichert werden": "Could not save rule",
        "Regel konnte nicht gelöscht werden": "Could not delete rule"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore } from '@/stores';
import GuestCustomerGroupRuleApi from '@/extensions/shop/store/api/guestCustomerGroupRule';

const blankRule = () => ({
    id: null,
    priority: 0,
    condition_type: 'country',
    condition_value: '',
    targetCustomerGroup: { id: null },
    domain: { id: null },
});

export default {
    name: 'GuestMarketRules',
    data() {
        return {
            rules: [],
            loading: false,
            saving: false,
            formVisible: false,
            editing: blankRule(),
            validationRules: {
                condition_type: 'required',
                condition_value: 'required',
                targetCustomerGroup: {
                    id: 'required',
                },
            },
        };
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms;
        },
        shopId() {
            const id = _.get(this.activeCms, 'shop.id');
            return id ? parseInt(id) : null;
        },
        domainOptions() {
            return _.get(this.activeCms, 'domains') || [];
        },
        guestEligibleGroups() {
            const groups = _.get(this.activeCms, 'shop.customerGroups') || [];
            return groups.filter(group => group.guest_eligible);
        },
    },
    mounted() {
        this.loadRules();
    },
    methods: {
        async loadRules() {
            if (!this.shopId) return;
            this.loading = true;
            try {
                const rules = await GuestCustomerGroupRuleApi.fetchAll({ shop: this.shopId });
                this.rules = rules || [];
            } catch (err) {
                console.error('Failed to load rules:', err);
            } finally {
                this.loading = false;
            }
        },
        handleRowClick(row) {
            this.openEdit(row);
        },
        openCreate() {
            this.editing = blankRule();
            this.formVisible = true;
        },
        openEdit(rule) {
            this.editing = {
                id: rule.id,
                priority: rule.priority,
                condition_type: rule.condition_type,
                condition_value: rule.condition_value,
                targetCustomerGroup: { id: _.get(rule, 'targetCustomerGroup.id', null) },
                domain: { id: _.get(rule, 'domain.id', null) },
            };
            this.formVisible = true;
        },
        async submit() {
            if (!this.editing) return;
            const isValid = await new Promise(resolve => this.$refs.ruleForm.validate(resolve));
            if (!isValid) return;
            this.saving = true;
            try {
                const payload = _.cloneDeep(this.editing);
                payload.shop = { id: this.shopId };
                if (!payload.domain.id) delete payload.domain;
                await GuestCustomerGroupRuleApi.save(payload);
                this.formVisible = false;
                await this.loadRules();
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Regel konnte nicht gespeichert werden'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.saving = false;
            }
        },
        askDelete(rule) {
            this.$confirm(this.$t('Diese Regel löschen?'), this.$t('Löschen'), { type: 'warning' })
                .then(() => this.confirmDelete(rule))
                .catch(() => {});
        },
        async confirmDelete(rule) {
            try {
                await GuestCustomerGroupRuleApi.delete(rule.id);
                await this.loadRules();
                this.$notify.success({
                    title: this.$t('Regel gelöscht'),
                    iconClass: 'fas el-icon-fa-check',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 5000,
                });
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Regel konnte nicht gelöscht werden'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.guest-market-rules
    .gmr-header
        display: flex
        align-items: flex-start
        justify-content: space-between
        gap: 1rem
        margin-bottom: 1rem

        &__left
            flex: 1
            min-width: 0

        &__title
            margin: 0 0 0.3rem 0
            font-size: 0.95rem
            font-weight: 600
            color: #303133

        &__hint
            margin: 0
            font-size: 0.8rem
            color: #909399
            line-height: 1.5

    .gmr-table
        margin-bottom: 0.5rem
        border-radius: 4px
        overflow: hidden

    .gmr-priority
        display: inline-flex
        align-items: center
        justify-content: center
        width: 28px
        height: 28px
        background: #f0f2f5
        border-radius: 50%
        font-size: 0.78rem
        font-weight: 600
        color: #606266

    .gmr-condition-value
        font-size: 0.8rem
        font-weight: 600
        color: #303133
        margin-left: 4px

    .gmr-group-name
        font-weight: 500
        color: #303133

    .gmr-currency
        color: #909399
        font-size: 0.85rem

    .gmr-all-domains
        color: #c0c4cc
        font-style: italic
        font-size: 0.85rem

    .gmr-empty
        padding: 2.5rem 1rem
        text-align: center

        &__icon
            font-size: 2rem
            color: #dcdfe6
            margin-bottom: 0.75rem

        &__text
            margin: 0 0 0.25rem 0
            font-size: 0.9rem
            color: #606266

        &__sub
            margin: 0
            font-size: 0.8rem
            color: #c0c4cc

    .gmr-field-hint
        margin: 6px 0 0 0
        font-size: 0.78rem
        color: #909399
        line-height: 1.5

    ::v-deep .el-dialog__body
        padding-top: 16px

        .gmr-form-row
            display: flex
            gap: 0.75rem
            align-items: flex-start

            .gmr-form-col
                margin-bottom: 18px
                margin-right: 0

                &--grow
                    flex: 1

            .el-input-number
                width: 110px

        .el-input-group__prepend
            padding: 0 10px
            color: #909399
</style>
